import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { naverMapService } from '@/services/naverMap';
import { locationService } from '@/services/location';
import type { LocationCoordinate } from '@/store/location';

export function MapLocationSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  // 초기 좌표 (location state에서 받아오거나 기본값)
  const initialCoordinate = location.state?.coordinate || {
    latitude: 37.5665, // 서울 시청 기본값
    longitude: 126.9780
  };
  
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [selectedCoordinate, setSelectedCoordinate] = useState<LocationCoordinate>(initialCoordinate);
  const [currentAddress, setCurrentAddress] = useState({
    roadAddress: '위치를 이동하여 주소를 선택하세요',
    jibunAddress: ''
  });
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate(-1);
  };

  // 주소 가져오기 (디바운스 적용)
  const fetchAddress = useCallback(async (coordinate: LocationCoordinate) => {
    setIsAddressLoading(true);
    try {
      const address = await locationService.reverseGeocode(coordinate);
      setCurrentAddress(address);
    } catch (error) {
      console.error('주소 변환 실패:', error);
      setCurrentAddress({
        roadAddress: '주소를 가져올 수 없습니다',
        jibunAddress: `위도 ${coordinate.latitude.toFixed(6)}, 경도 ${coordinate.longitude.toFixed(6)}`
      });
    } finally {
      setIsAddressLoading(false);
    }
  }, []);

  // 지도 이동 이벤트 핸들러
  const handleMapMove = useCallback(() => {
    if (!mapRef.current) return;
    
    const center = naverMapService.getMapCenter(mapRef.current);
    setSelectedCoordinate(center);
    
    // 디바운스 처리 (300ms)
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      fetchAddress(center);
    }, 300);
    
    setDebounceTimer(timer);
  }, [debounceTimer, fetchAddress]);

  // 지도 초기화
  useEffect(() => {
    const initMap = async () => {
      if (!mapContainerRef.current) return;
      
      try {
        setIsMapLoading(true);
        console.log('지도 초기화 시작...');
        
        // 네이버 지도 스크립트 로드
        await naverMapService.loadMapScript();
        console.log('네이버 지도 스크립트 로드 완료');
        
        // 지도 생성
        const map = await naverMapService.createMap('map-container', {
          center: initialCoordinate,
          zoom: 16,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver?.maps?.Position?.TOP_RIGHT
          }
        });
        
        console.log('지도 생성 완료');
        mapRef.current = map;
        
        // 지도 이동 이벤트 리스너 추가
        window.naver.maps.Event.addListener(map, 'idle', handleMapMove);
        
        // 초기 주소 가져오기
        await fetchAddress(initialCoordinate);
        
      } catch (error) {
        console.error('지도 초기화 실패:', error);
        setIsMapLoading(false);
        
        // 상세한 에러 메시지
        let errorMessage = '지도를 불러올 수 없습니다.';
        if (error instanceof Error) {
          if (error.message.includes('클라이언트 ID')) {
            errorMessage = '네이버 지도 API 키가 설정되지 않았습니다.';
          } else if (error.message.includes('네트워크')) {
            errorMessage = '네트워크 연결을 확인해주세요.';
          }
        }
        alert(errorMessage);
      } finally {
        setIsMapLoading(false);
      }
    };
    
    initMap();
    
    // 클린업
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  // 위치 설정 버튼 핸들러
  const handleConfirmLocation = () => {
    navigate('/address-detail', {
      state: {
        selectedAddress: {
          roadAddress: currentAddress.roadAddress,
          jibunAddress: currentAddress.jibunAddress,
          coordinate: selectedCoordinate
        }
      }
    });
  };

  return (
    <div className="flex flex-col h-screen bg-white relative">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10 bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-4">
          <button
            onClick={handleBack}
            className="p-0 bg-transparent"
          >
            <img src="/icons/icon_back.svg" alt="뒤로가기" width="24" height="24" />
          </button>
          
          <h1 className="text-[24px] font-bold text-[#222222]">주소 설정</h1>
          
          <div className="w-6" />
        </div>
      </header>

      {/* Map Container - 전체 화면 */}
      <div className="flex-1 relative mt-[60px]">
        <div 
          id="map-container" 
          ref={mapContainerRef}
          className="w-full h-full"
        />
        
        {/* 중앙 고정 핀 마커 - 네이버 기본 마커 스타일 */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full pointer-events-none" style={{ marginTop: '-10px' }}>
          <div className="relative">
            {/* 네이버 기본 마커 모양 구현 */}
            <div className="w-[29px] h-[42px] relative">
              {/* 마커 몸체 */}
              <div className="absolute inset-0 bg-[#00E676] rounded-t-full" 
                style={{
                  clipPath: 'polygon(50% 100%, 0% 40%, 0% 20%, 10% 5%, 30% 0%, 70% 0%, 90% 5%, 100% 20%, 100% 40%)',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}>
              </div>
              {/* 마커 중앙 흰색 원 */}
              <div className="absolute top-[8px] left-1/2 transform -translate-x-1/2 w-[13px] h-[13px] bg-white rounded-full"></div>
            </div>
          </div>
        </div>

        {/* 로딩 인디케이터 */}
        {isMapLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#86DB89] mx-auto mb-4"></div>
              <p className="text-gray-600">지도를 불러오는 중...</p>
            </div>
          </div>
        )}
        
        {/* 지도 로드 실패 시 대체 UI */}
        {!isMapLoading && !mapRef.current && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="text-center p-4">
              <p className="text-lg font-semibold text-gray-700 mb-2">지도를 표시할 수 없습니다</p>
              <p className="text-sm text-gray-600 mb-4">
                네이버 클라우드 플랫폼에서<br />
                localhost 도메인을 등록해주세요
              </p>
              <p className="text-xs text-gray-500">
                현재 위치: {selectedCoordinate.latitude.toFixed(6)}, {selectedCoordinate.longitude.toFixed(6)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Section - 주소 표시 및 버튼 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <div className="px-4 py-4">
          {/* 현재 위치 주소 표시 */}
          <div className="mb-4">
            <div className="text-sm text-gray-600 mb-1">현 위치 도로명 주소</div>
            <div className="text-base font-medium text-[#222222] min-h-[24px]">
              {isAddressLoading ? (
                <span className="text-gray-400">주소를 가져오는 중...</span>
              ) : (
                currentAddress.roadAddress
              )}
            </div>
            {currentAddress.jibunAddress && (
              <>
                <div className="text-sm text-gray-600 mt-2 mb-1">현 위치 지번 주소</div>
                <div className="text-base font-medium text-[#222222]">
                  {currentAddress.jibunAddress}
                </div>
              </>
            )}
          </div>

          {/* 여기로 위치 설정 버튼 */}
          <button
            onClick={handleConfirmLocation}
            disabled={isAddressLoading || !currentAddress.roadAddress || currentAddress.roadAddress === '위치를 이동하여 주소를 선택하세요'}
            className="w-full h-12 bg-[#DEDEDE] rounded-xl flex items-center justify-center transition-all hover:bg-gray-300 active:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed"
          >
            <span className="text-xl font-semibold text-[#222222]">여기로 위치 설정</span>
          </button>
        </div>
      </div>
    </div>
  );
}